import { useEffect, useId, useRef } from 'react'
import { ArrowDownLeft, ArrowUpRight, SquareSplitHorizontal, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export type AddChoice =
  | 'SPLIT'
  | 'PAID_FOR_THEM'
  | 'THEY_PAID'
  | 'LENT'
  | 'BORROWED'
  | 'GIVE_LOAN'
  | 'TAKE_LOAN'

/** Phone: a bottom sheet. sm+: a centred dialog. */
export function AddWithPersonSheet({
  name,
  showLoans,
  onPick,
  onClose,
}: {
  name: string
  showLoans: boolean
  onPick: (choice: AddChoice) => void
  onClose: () => void
}) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('button[data-choice]')?.focus()
  }, [])

  const shareCost: [AddChoice, string, string, LucideIcon, boolean][] = [
    ['SPLIT', 'Split a bill', `You paid, ${name} owes a share`, SquareSplitHorizontal, true],
    [
      'PAID_FOR_THEM',
      `Paid for ${name}`,
      `All of it was ${name}’s — they owe you the full amount`,
      ArrowUpRight,
      true,
    ],
    [
      'THEY_PAID',
      `${name} paid for me`,
      `Counts as your expense; you owe ${name}`,
      ArrowDownLeft,
      false,
    ],
  ]
  const lendBorrow: [AddChoice, string, string][] = [
    ['LENT', 'Lent money', 'No interest'],
    ['BORROWED', 'Borrowed money', 'No interest'],
    ...(showLoans
      ? ([
          ['GIVE_LOAN', 'Give a loan…', 'With interest, tracked in Loans'],
          ['TAKE_LOAN', 'Take a loan…', 'With interest, tracked in Loans'],
        ] as [AddChoice, string, string][])
      : []),
  ]

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="w-full rounded-t-[20px] bg-[var(--fl-surface)] px-4 pt-2 pb-[max(24px,env(safe-area-inset-bottom))] shadow-[rgba(0,0,0,0.15)_0px_8px_40px] motion-safe:animate-fade-slide-up sm:mx-4 sm:max-w-md sm:rounded-[20px] sm:p-6"
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--fl-border)] sm:hidden"
        />
        <div className="mb-2 flex items-center justify-between">
          <h2 id={titleId} className="font-serif text-xl font-semibold text-[var(--fl-heading)]">
            Add with {name}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
          >
            <X size={20} />
          </button>
        </div>

        <div role="group" aria-labelledby={`${titleId}-share`}>
          <p
            id={`${titleId}-share`}
            className="mt-2 mb-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--fl-text-2)]"
          >
            Share a cost
          </p>
          {shareCost.map(([choice, label, hint, Icon, owedToUs]) => (
            <button
              key={choice}
              type="button"
              data-choice={choice}
              onClick={() => onPick(choice)}
              className="flex min-h-14 w-full items-center gap-3 rounded-xl px-1 py-2.5 text-left hover:bg-[var(--fl-surface-hover)]"
            >
              <span
                aria-hidden="true"
                className={cn(
                  'grid h-9 w-9 shrink-0 place-items-center rounded-[10px]',
                  owedToUs
                    ? 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]'
                    : 'bg-[var(--fl-expense-bg)] text-[var(--fl-expense)]'
                )}
              >
                <Icon size={18} />
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-medium text-[var(--fl-text-1)]">
                  {label}
                </span>
                <span className="block text-xs text-[var(--fl-text-3)]">{hint}</span>
              </span>
            </button>
          ))}
        </div>

        <div role="group" aria-labelledby={`${titleId}-lend`}>
          <p
            id={`${titleId}-lend`}
            className="mt-4 mb-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--fl-text-2)]"
          >
            Lend or borrow
          </p>
          <div className="grid grid-cols-2 gap-2">
            {lendBorrow.map(([choice, label, hint]) => (
              <button
                key={choice}
                type="button"
                data-choice={choice}
                onClick={() => onPick(choice)}
                className="min-h-16 rounded-xl border border-[var(--fl-border)] px-3 py-2.5 text-left hover:bg-[var(--fl-surface-hover)]"
              >
                <span className="block text-sm font-medium text-[var(--fl-text-1)]">{label}</span>
                <span className="block text-xs text-[var(--fl-text-3)]">{hint}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
